import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { CrudTable, type CrudColumn } from '@/shared/components/ui/crud-table';
import { useIsAdminTier } from '@/shared/auth/SessionProvider';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { StatusBadge } from '@/shared/components/ui/status-badge';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Collapsible } from '@/shared/components/ui/collapsible';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Combobox } from '@/shared/components/ui/combobox';
import {
  createProveedor,
  listAllProveedores,
  softDeleteProveedor,
  updateProveedor,
  type ProveedorItem,
} from '@/shared/api/controllers/proveedores';
import { listActiveRetenciones } from '@/shared/api/controllers/retenciones';
import {
  CONDICIONES_IVA,
  ESTADOS_MX,
  PROVINCIAS_ARGENTINA,
} from '@/features/contactos/constants';
import { invalidateAbmRelatedQueries } from '@/shared/api/abmCacheInvalidation';
import type { EntityViewProps } from '../../types';

const QUERY_KEY = ['configuracion', 'proveedores'] as const;

const TIPOS_IDENTIFICACION = ['CUIT', 'CUIL', 'RFC'] as const;
const CONDICIONES_PAGO = ['Contado', '7 Días', '10 Días', '14 Días', '15 Días', '30 Días'] as const;
const TIPOS_RETENCIONES = [
  'Ganancias',
  'Ingresos Brutos',
  'IVA',
  'Seguridad Social',
  'Otros',
] as const;

type Mode =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; item: ProveedorItem }
  | { kind: 'delete'; item: ProveedorItem };

const schema = z
  .object({
    nombre: z.string().trim().min(1, 'Ingresá el nombre'),
    tipo: z.enum(TIPOS_IDENTIFICACION),
    identificacion: z.string().optional(),
    condicionIVA: z.string().optional(),
    condicionPago: z.string().optional(),
    email: z
      .string()
      .trim()
      .optional()
      .refine((v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), {
        message: 'Formato de email inválido',
      }),
    telefono: z.string().optional(),
    pais: z.string().optional(),
    provincia: z.string().optional(),
    localidad: z.string().optional(),
    direccionCompleta: z.string().optional(),
    recibeRetenciones: z.boolean(),
    tipoRetenciones: z.string().optional(),
    conceptoRetencion: z.string().optional(),
    esFabricante: z.boolean(),
  })
  .superRefine((v, ctx) => {
    if (v.recibeRetenciones) {
      if (!v.tipoRetenciones) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['tipoRetenciones'],
          message: 'Seleccioná el tipo de retención',
        });
      }
      if (!v.conceptoRetencion) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['conceptoRetencion'],
          message: 'Seleccioná un concepto',
        });
      }
    }
  });

type Values = z.infer<typeof schema>;

function emptyFor(country: 'AR' | 'MX'): Values {
  return {
    nombre: '',
    tipo: country === 'MX' ? 'RFC' : 'CUIT',
    identificacion: '',
    condicionIVA: '',
    condicionPago: '',
    email: '',
    telefono: '',
    pais: country === 'MX' ? 'México' : 'Argentina',
    provincia: '',
    localidad: '',
    direccionCompleta: '',
    recibeRetenciones: false,
    tipoRetenciones: '',
    conceptoRetencion: '',
    esFabricante: false,
  };
}

function itemToForm(item: ProveedorItem): Values {
  const f = item.fields;
  return {
    nombre: f.Nombre_P ?? '',
    tipo: (f.Tipo_P as 'CUIT' | 'CUIL') === 'CUIL' ? 'CUIL' : 'CUIT',
    identificacion: f.Identificacion_P ?? '',
    condicionIVA: f.CondicionIVA_P ?? '',
    condicionPago: f.CondicionPago_P ?? '',
    email: f.Email_P ?? '',
    telefono: f.Telefono_P ?? '',
    pais: f.Pais_P ?? 'Argentina',
    provincia: f.Provincia_P ?? '',
    localidad: f.Localidad_P ?? '',
    direccionCompleta: f.DireccionCompleta_P ?? '',
    recibeRetenciones: f.RecibeRetenciones_P === 'SI',
    tipoRetenciones: f.TipoRetenciones_P ?? '',
    conceptoRetencion: f.Concepto_P ?? '',
    esFabricante: f.EsFabricante_P === 'SI',
  };
}

export function ProveedoresEntity({
  search,
  filterValue,
  onStatus,
  openCreate,
  onCreateHandled,
}: EntityViewProps) {
  const queryClient = useQueryClient();
  // Country code appended so AR/MX caches don't share a slot.
  // `queryClient.clear()` on country switch already prevents staleness
  // globally; this is defense-in-depth for direct `getQueryData` reads.
  const { config: activeCountry } = useActiveCountry();
  const scopedKey = useMemo(
    () => [...QUERY_KEY, activeCountry.code] as readonly unknown[],
    [activeCountry.code],
  );
  const { data: rows = [], isPending, isFetching, error, refetch } = useQuery({
    queryKey: scopedKey,
    queryFn: listAllProveedores,
    staleTime: 60_000,
  });
  const [mode, setMode] = useState<Mode>({ kind: 'closed' });

  useEffect(() => {
    if (openCreate) {
      setMode({ kind: 'create' });
      onCreateHandled();
    }
  }, [openCreate, onCreateHandled]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let sorted = [...rows].sort((a, b) =>
      (a.fields.Nombre_P ?? '').localeCompare(b.fields.Nombre_P ?? '', 'es'),
    );
    if (filterValue) {
      sorted = sorted.filter((r) => r.fields.Status_P === filterValue);
    }
    if (!q) return sorted;
    return sorted.filter((r) => {
      const n = (r.fields.Nombre_P ?? '').toLowerCase();
      const id = (r.fields.Identificacion_P ?? '').toLowerCase();
      const email = (r.fields.Email_P ?? '').toLowerCase();
      return n.includes(q) || id.includes(q) || email.includes(q);
    });
  }, [rows, search, filterValue]);

  const isMx = activeCountry.code === 'MX';
  const columns: CrudColumn<ProveedorItem>[] = [
    { header: 'Status', width: 'w-32', render: (r) => <StatusBadge status={r.fields.Status_P} /> },
    {
      header: 'Nombre',
      render: (r) => <span className="font-medium text-foreground">{r.fields.Nombre_P || '—'}</span>,
    },
    {
      header: isMx ? 'RFC' : 'CUIT / CUIL',
      render: (r) => (
        <span className="text-muted-foreground">{r.fields.Identificacion_P || '—'}</span>
      ),
    },
    {
      header: 'Condición IVA',
      render: (r) => <span className="text-muted-foreground">{r.fields.CondicionIVA_P || '—'}</span>,
    },
    {
      header: 'Cond. de pago',
      render: (r) => <span className="text-muted-foreground">{r.fields.CondicionPago_P || '—'}</span>,
    },
  ];

  const closeDialog = () => setMode({ kind: 'closed' });
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    invalidateAbmRelatedQueries(queryClient, 'proveedores');
  };

  const canEdit = useIsAdminTier();
  return (
    <>
      <CrudTable
        hideActions={!canEdit}
        rows={filtered}
        columns={columns}
        loading={isPending}
        error={error instanceof Error ? error : null}
        onRetry={() => refetch()}
        emptyMessage={search ? `No hay resultados para "${search}".` : 'Sin proveedores.'}
        onEdit={(r) => setMode({ kind: 'edit', item: r })}
        onDelete={(r) => setMode({ kind: 'delete', item: r })}
        canEdit={(r) => r.fields.Status_P !== 'Inactivo'}
        editDisabledTitle={() => 'El registro está inactivo'}
        canDelete={(r) => r.fields.Status_P !== 'Inactivo'}
        deleteDisabledTitle={() => 'El registro ya está inactivo'}
        isBackgroundFetching={isFetching}
      />

      {(mode.kind === 'create' || mode.kind === 'edit') && (
        <ProveedorDialog
          open
          onOpenChange={(o) => !o && closeDialog()}
          item={mode.kind === 'edit' ? mode.item : null}
          existingRows={rows}
          onSuccess={(msg) => {
            invalidate();
            closeDialog();
            onStatus({ kind: 'success', message: msg });
          }}
          onError={(msg) => {
            closeDialog();
            onStatus({ kind: 'error', message: msg });
          }}
        />
      )}

      {mode.kind === 'delete' && (
        <DeleteProveedorDialog
          open
          onOpenChange={(o) => !o && closeDialog()}
          item={mode.item}
          onSuccess={(msg) => {
            invalidate();
            closeDialog();
            onStatus({ kind: 'success', message: msg });
          }}
          onError={(msg) => {
            closeDialog();
            onStatus({ kind: 'error', message: msg });
          }}
        />
      )}
    </>
  );
}

function ProveedorDialog({
  open,
  onOpenChange,
  item,
  existingRows,
  onSuccess,
  onError,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  item: ProveedorItem | null;
  existingRows: ProveedorItem[];
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const isEdit = Boolean(item);
  const { config: activeCountry } = useActiveCountry();
  const showRetenciones = activeCountry.features.has('retenciones');
  const isMx = activeCountry.code === 'MX';
  // País + Tipo defaults follow the active country so a new MX
  // proveedor starts with México / RFC instead of Argentina / CUIT.
  const empty = useMemo(() => emptyFor(activeCountry.code), [activeCountry.code]);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: item ? itemToForm(item) : empty,
  });

  useEffect(() => {
    if (open) form.reset(item ? itemToForm(item) : empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  const { data: retenciones = [] } = useQuery({
    queryKey: ['configuracion', 'retenciones', 'active', activeCountry.code],
    queryFn: listActiveRetenciones,
    staleTime: 60_000,
    enabled: showRetenciones,
  });

  const recibeRetenciones = form.watch('recibeRetenciones');
  const watchedNombre = form.watch('nombre');
  const watchedTipoRet = form.watch('tipoRetenciones');
  const watchedConcepto = form.watch('conceptoRetencion');

  const missingFields = useMemo(() => {
    const missing: string[] = [];
    if (!watchedNombre?.trim()) missing.push('Nombre');
    if (recibeRetenciones) {
      if (!watchedTipoRet) missing.push('Tipo de retención');
      if (!watchedConcepto) missing.push('Concepto');
    }
    return missing;
  }, [watchedNombre, recibeRetenciones, watchedTipoRet, watchedConcepto]);

  const canSubmit = missingFields.length === 0;

  const mutation = useMutation({
    mutationFn: async (v: Values) => {
      const payload = {
        nombre: v.nombre,
        tipo: v.tipo,
        identificacion: v.identificacion,
        condicionIVA: v.condicionIVA,
        condicionPago: v.condicionPago,
        email: v.email,
        telefono: v.telefono,
        pais: v.pais,
        provincia: v.provincia,
        localidad: v.localidad,
        direccionCompleta: v.direccionCompleta,
        recibeRetenciones: v.recibeRetenciones,
        tipoRetenciones: v.tipoRetenciones,
        conceptoRetencion: v.conceptoRetencion,
        esFabricante: v.esFabricante,
      };
      if (isEdit && item) return updateProveedor(item.id, payload);
      return createProveedor(payload);
    },
    onSuccess: () =>
      onSuccess(
        isEdit
          ? 'El proveedor se actualizó correctamente.'
          : 'El proveedor se creó correctamente.',
      ),
    onError: (err) => onError(err instanceof Error ? err.message : 'No se pudo guardar el proveedor.'),
  });

  const onSubmit = form.handleSubmit((v) => {
    const norm = v.nombre.trim().toLowerCase();
    const duplicated = existingRows.find(
      (r) =>
        r.id !== item?.id &&
        (r.fields.Nombre_P ?? '').trim().toLowerCase() === norm,
    );
    if (duplicated) {
      form.setError('nombre', {
        type: 'manual',
        message: 'Ya existe un proveedor con ese nombre.',
      });
      return;
    }
    mutation.mutate(v);
  });

  const fieldError = (name: keyof Values) =>
    form.formState.errors[name]?.message as string | undefined;

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-3xl"
        processing={mutation.isPending && (isEdit ? 'Guardando cambios…' : 'Creando proveedor…')}
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar proveedor' : 'Nuevo proveedor'}</DialogTitle>
          <DialogDescription>
            Completá los datos del proveedor. Las secciones son colapsables.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-1 flex-col overflow-hidden" noValidate>
          <DialogBody className="space-y-4">
            <Collapsible title="Datos del proveedor" defaultOpen>
              <div className="grid gap-4 md:grid-cols-3">
                <Field label="Nombre" required error={fieldError('nombre')} className="md:col-span-2">
                  <Input
                    variant="filled"
                    placeholder="Ingresar Nombre"
                    autoFocus
                    {...form.register('nombre')}
                  />
                </Field>
                <Field label="Tipo" error={fieldError('tipo')}>
                  <Select
                    value={form.watch('tipo')}
                    onValueChange={(v) =>
                      form.setValue('tipo', v as Values['tipo'], { shouldDirty: true })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS_IDENTIFICACION.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Identificación" error={fieldError('identificacion')}>
                  <Input
                    variant="filled"
                    placeholder="00-00000000-0"
                    {...form.register('identificacion')}
                  />
                </Field>
                <Field label="Email" error={fieldError('email')}>
                  <Input
                    variant="filled"
                    type="email"
                    placeholder="proveedor@example.com"
                    {...form.register('email')}
                  />
                </Field>
                <Field label="Teléfono" error={fieldError('telefono')}>
                  <Input
                    variant="filled"
                    placeholder="Ingresar teléfono"
                    {...form.register('telefono')}
                  />
                </Field>
              </div>
            </Collapsible>

            <Collapsible title="Condiciones comerciales" defaultOpen>
              <div className="grid gap-4 md:grid-cols-2">
                <Field
                  label={isMx ? 'Régimen Fiscal' : 'Condición IVA'}
                  error={fieldError('condicionIVA')}
                >
                  {isMx ? (
                    // MX SAT regime catalog isn't hardcoded yet — let
                    // the operator type whatever the proveedor's RFC
                    // certificate carries. Promote to a dropdown when
                    // we have the catalog.
                    <Input
                      variant="filled"
                      placeholder="Régimen del SAT"
                      value={form.watch('condicionIVA') ?? ''}
                      onChange={(e) =>
                        form.setValue('condicionIVA', e.target.value, {
                          shouldDirty: true,
                        })
                      }
                    />
                  ) : (
                    <Select
                      value={form.watch('condicionIVA') ?? ''}
                      onValueChange={(v) =>
                        form.setValue('condicionIVA', v, { shouldDirty: true })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar condición IVA" />
                      </SelectTrigger>
                      <SelectContent>
                        {CONDICIONES_IVA.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
                <Field label="Condición de pago" error={fieldError('condicionPago')}>
                  <Select
                    value={form.watch('condicionPago') ?? ''}
                    onValueChange={(v) =>
                      form.setValue('condicionPago', v, { shouldDirty: true })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar condición" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONDICIONES_PAGO.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <label className="flex cursor-pointer items-start gap-3 rounded-lg border bg-secondary/40 p-3">
                <input
                  type="checkbox"
                  checked={form.watch('esFabricante')}
                  onChange={(e) => form.setValue('esFabricante', e.target.checked, { shouldDirty: true })}
                  className="mt-1 h-4 w-4 accent-primary"
                />
                <span className="flex flex-col">
                  <span className="text-sm font-medium text-foreground">Es fabricante</span>
                  <span className="text-xs text-muted-foreground">
                    Sólo los fabricantes se pueden elegir en Importaciones.
                  </span>
                </span>
              </label>

              {showRetenciones && (
                <label className="flex cursor-pointer items-start gap-3 rounded-lg border bg-secondary/40 p-3">
                  <input
                    type="checkbox"
                    checked={recibeRetenciones}
                    onChange={(e) =>
                      form.setValue('recibeRetenciones', e.target.checked, { shouldDirty: true })
                    }
                    className="mt-1 h-4 w-4 accent-primary"
                  />
                  <span className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">Recibe retenciones</span>
                    <span className="text-xs text-muted-foreground">
                      Si está activo, seleccioná el tipo y el concepto a aplicar.
                    </span>
                  </span>
                </label>
              )}

              {showRetenciones && recibeRetenciones && (
                <div className="grid gap-4 md:grid-cols-2">
                  <Field
                    label="Tipo de retención"
                    required
                    error={fieldError('tipoRetenciones')}
                  >
                    <Select
                      value={form.watch('tipoRetenciones') ?? ''}
                      onValueChange={(v) =>
                        form.setValue('tipoRetenciones', v, { shouldDirty: true, shouldValidate: true })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIPOS_RETENCIONES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field
                    label="Concepto"
                    required
                    error={fieldError('conceptoRetencion')}
                  >
                    {(() => {
                      const selectedConcepto = form.watch('conceptoRetencion') ?? '';
                      const selectedItem =
                        retenciones.find(
                          (r) =>
                            (r.fields.ConcatCodigoRegimenConcepto_R ?? '') ===
                            selectedConcepto,
                        ) ?? null;
                      return (
                        <Combobox
                          value={selectedItem}
                          onChange={(next) =>
                            form.setValue(
                              'conceptoRetencion',
                              next?.fields.ConcatCodigoRegimenConcepto_R ?? '',
                              { shouldDirty: true, shouldValidate: true },
                            )
                          }
                          items={retenciones}
                          getKey={(r) => r.id}
                          getLabel={(r) =>
                            r.fields.ConcatCodigoRegimenConcepto_R ?? '—'
                          }
                          matcher={(r, q) =>
                            (r.fields.ConcatCodigoRegimenConcepto_R ?? '')
                              .toLowerCase()
                              .includes(q)
                          }
                          placeholder="Seleccionar concepto"
                          emptyMessage="Sin conceptos coincidentes."
                        />
                      );
                    })()}
                  </Field>
                </div>
              )}
            </Collapsible>

            <Collapsible title="Domicilio" defaultOpen={false}>
              <div className="grid gap-4 md:grid-cols-3">
                <Field label="País">
                  <Input variant="filled" {...form.register('pais')} />
                </Field>
                <Field label={isMx ? 'Estado' : 'Provincia'}>
                  <Select
                    value={form.watch('provincia') ?? ''}
                    onValueChange={(v) =>
                      form.setValue('provincia', v, { shouldDirty: true })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={isMx ? 'Seleccionar estado' : 'Seleccionar provincia'}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {(isMx ? ESTADOS_MX : PROVINCIAS_ARGENTINA).map((p) => (
                        <SelectItem key={p} value={p}>
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Localidad">
                  <Input variant="filled" {...form.register('localidad')} />
                </Field>
              </div>
              <Field label="Dirección completa">
                <Input
                  variant="filled"
                  placeholder="Ingresar Dirección completa"
                  {...form.register('direccionCompleta')}
                />
              </Field>
            </Collapsible>
          </DialogBody>
          <DialogFooter>
            {missingFields.length > 0 && (
              <p className="mr-auto text-xs text-muted-foreground">
                Faltan completar: <span className="font-medium text-destructive">{missingFields.join(', ')}</span>
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending || !canSubmit}>
              {isEdit ? 'Guardar cambios' : 'Añadir'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  required,
  error,
  children,
  className,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={['space-y-1.5', className].filter(Boolean).join(' ')}>
      <Label>
        {label}
        {required && <span className="text-primary"> *</span>}
      </Label>
      {children}
      {error && <p className="text-xs font-medium text-destructive">{error}</p>}
    </div>
  );
}

function DeleteProveedorDialog({
  open,
  onOpenChange,
  item,
  onSuccess,
  onError,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  item: ProveedorItem;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const mutation = useMutation({
    mutationFn: async () => softDeleteProveedor(item.id),
    onSuccess: () => onSuccess('Proveedor eliminado correctamente.'),
    onError: (err) => onError(err instanceof Error ? err.message : 'No se pudo eliminar el proveedor.'),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-md"
        processing={mutation.isPending && 'Eliminando proveedor…'}
      >
        <DialogHeader>
          <DialogTitle>Eliminar proveedor</DialogTitle>
          <DialogDescription>
            ¿Confirmás la eliminación de{' '}
            <strong className="text-foreground">{item.fields.Nombre_P || '—'}</strong>?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            Eliminar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
